import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Layout } from 'antd';

import styled from 'styled-components';

import { removeStatementLog } from '@libs/NwLogs';

import { forceReloadPayments, usePayment } from '@hooks/API/UsePayments';

import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwResult from '@components/Gui/NwResult';
import EditPaymentDrawer from '@components/Payments/PaymentPage/EditPaymentDrawer';
import PaymentPageHeader from '@components/Payments/PaymentPage/PaymentPageHeader';
import PaymentPageSider from '@components/Payments/PaymentPage/PaymentPageSider';
import PaymentPageTop from '@components/Payments/PaymentPage/PaymentPageTop';

import PaymentSentStatus from './PaymentSentStatus';
import PaymentStatementLines from './PaymentStatementLines';

const StyledLayout = styled(Layout)`
    min-height: ${(props) => (props.inDrawer ? '0' : 'calc(100vh - 220px)')};
    padding: ${(props) => (props.inDrawer ? '0' : '8px 16px 16px')};
    background: white;
    margin: 0 1rem;
    display: flex;
    flex-direction: row;
`;

const PaymentContent = styled.div`
    width: 100%;
    padding: 1rem 2rem 1rem 4rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`;

const PaymentPage = ({ paymentID, contactType = 'model', inDrawer, onClose }) => {
    const navigate = useNavigate();
    const { data: paymentData, isFetching, error } = usePayment(paymentID);

    const [showEditDrawer, setShowEditDrawer] = useState(false);

    useEffect(() => {
        if (error) {
            removeStatementLog(paymentID);
        }
    }, [error]);

    const handleDeletePayment = () => {
        if (inDrawer) {
            onClose();
        } else {
            forceReloadPayments(paymentData.ContactID);
            navigate(`/accounting-modelpayments`);
        }
    };

    // Dynamic error message based on contact type
    const getDeletedMessage = () => {
        switch (contactType) {
            case 'model':
                return 'This model payment has been deleted';
            case 'agency':
                return 'This agency payment has been deleted';
            case 'service':
                return 'This service payment has been deleted';
            default:
                return 'This payment has been deleted';
        }
    };

    if (error) {
        return <NwResult status="error" title={getDeletedMessage()} />;
    }

    return !isFetching ? (
        <div>
            {paymentData && (
                <>
                    <PaymentPageHeader
                        paymentId={paymentID}
                        contactType={contactType}
                        inDrawer={inDrawer}
                        handleShowEditDrawer={() => setShowEditDrawer(true)}
                        onDelete={handleDeletePayment}
                    />
                    <StyledLayout $inDrawer={inDrawer}>
                        <PaymentPageSider paymentData={paymentData} onShowEditDrawer={() => setShowEditDrawer(true)} />
                        <PaymentContent>
                            {(paymentData.ReadyToPost || paymentData.Posted) && (
                                <PaymentSentStatus paymentData={paymentData} />
                            )}
                            <PaymentPageTop paymentData={paymentData} />
                            <NwDivider>PAYMENT DESCRIPTION</NwDivider>
                            <div>
                                <p>{paymentData.HeaderNote}</p>
                                <NwLinkButton
                                    onClick={() => setShowEditDrawer(false)}
                                    label={`${paymentData.HeaderNote ? 'edit' : 'add'} payment description`}
                                />
                            </div>
                            <NwDivider>PAYMENT LINES</NwDivider>
                            <PaymentStatementLines paymentData={paymentData} />
                        </PaymentContent>
                    </StyledLayout>
                    {showEditDrawer && (
                        <EditPaymentDrawer onClose={() => setShowEditDrawer(false)} paymentData={paymentData} />
                    )}
                </>
            )}
        </div>
    ) : (
        <Loading />
    );
};

export default PaymentPage;
